<template>
  <v-container class="team-users-page" fluid>
    <header class="team-users-heading">
      <div><p class="portal-eyebrow">Account access</p><h1>Users</h1><p>Invite and manage the people who can create and maintain tours for this account.</p></div>
      <div class="team-users-count"><v-icon icon="mdi-account-group-outline" size="22" aria-hidden="true" /><span><strong>{{ users.length }}</strong><small>{{ users.length === 1 ? 'user' : 'users' }}</small></span></div>
    </header>

    <section class="team-users-layout">
      <v-card class="team-users-card team-users-card--invite" variant="flat">
        <v-card-title class="team-users-card__title"><span class="team-users-card__icon"><v-icon icon="mdi-account-plus-outline" size="22" aria-hidden="true" /></span><div><strong>Add a user</strong><small>Give someone access to this account.</small></div></v-card-title>
        <v-card-text class="team-users-card__body">
          <form @submit.prevent="createUser">
            <p class="team-users-intro">New users can sign in and add other customer users.</p>
            <v-alert v-if="error" type="error" variant="tonal" class="mb-4" role="alert">{{ error }}</v-alert>
            <v-alert v-if="success" type="success" variant="tonal" class="mb-4" role="status">{{ success }}</v-alert>
            <div class="team-users-form">
              <v-text-field v-model="newUser.name" label="Full name" variant="outlined" density="comfortable" hide-details />
              <v-text-field v-model="newUser.email" label="Email address" type="email" autocomplete="off" variant="outlined" density="comfortable" hide-details />
              <v-text-field v-model="newUser.password" label="Temporary password" variant="outlined" density="comfortable" type="password" autocomplete="new-password" hint="At least 6 characters, including uppercase, lowercase and a number." persistent-hint />
            </div>
            <div class="team-users-form__actions"><v-btn color="primary" type="submit" prepend-icon="mdi-account-plus-outline" :loading="creating" :disabled="creating">Add user</v-btn></div>
          </form>
        </v-card-text>
      </v-card>

      <v-card class="team-users-card team-users-card--list" variant="flat">
          <v-card-title class="team-users-card__title"><span class="team-users-card__icon"><v-icon icon="mdi-account-group-outline" size="22" aria-hidden="true" /></span><div><strong>People with access</strong><small>Manage login details and passwords.</small></div></v-card-title>
          <v-data-table
            :headers="userHeaders"
            :items="users"
            item-key="id"
            density="comfortable"
            class="team-users-table"
          >
            <!-- <template #item.actions="{ item }" >
              <v-select
                :model-value="item.role"
                :items="availableRoles"
                label="Role"
                dense
                @update:modelValue="(val) => onRoleChange(item, val)"
              />
            </template>
            -->

            <template #item.role="{ item }"><v-chip size="small" color="primary" variant="tonal">{{ item.role || 'User' }}</v-chip></template>
            <template #item.delete="{ item }">
              <div v-if="canManageUsers" class="team-users-actions">
                <v-btn variant="text" size="small" prepend-icon="mdi-lock-reset" @click="openPasswordChange(item)">Password</v-btn>
                <v-btn variant="text" size="small" prepend-icon="mdi-pencil-outline" @click="editUser(item)">Edit</v-btn>
                <v-btn variant="text" size="small" color="error" icon="mdi-delete-outline" :aria-label="`Delete ${item.fullName || item.email}`" :title="`Delete ${item.fullName || item.email}`" @click="onDeleteUser(item)" />
              </div>
            </template>
          </v-data-table>
      </v-card>
    </section>
    <v-dialog v-model="passwordOpen" max-width="500" persistent>
      <v-card title="Change password">
        <form @submit.prevent="savePassword">
          <v-card-text>
            <p class="mb-4">Set a new password for {{ passwordUser?.fullName || passwordUser?.email }}.</p>
            <v-alert v-if="passwordError" type="error" variant="tonal" class="mb-4" role="alert">{{ passwordError }}</v-alert>
            <v-text-field v-model="newPassword" label="New password" type="password" autocomplete="new-password" minlength="6" maxlength="128" required :disabled="savingPassword" hint="At least 6 characters, including uppercase, lowercase and a number." persistent-hint class="mb-3" />
            <v-text-field v-model="confirmPassword" label="Confirm new password" type="password" autocomplete="new-password" maxlength="128" required :disabled="savingPassword" />
          </v-card-text>
          <v-card-actions><v-spacer /><v-btn :disabled="savingPassword" @click="closePasswordChange">Cancel</v-btn><v-btn type="submit" color="primary" :loading="savingPassword" :disabled="savingPassword">Change password</v-btn></v-card-actions>
        </form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editOpen" max-width="500" persistent>
      <v-card title="Edit user">
        <form @submit.prevent="saveUser">
          <v-card-text>
            <v-alert v-if="editError" type="error" variant="tonal" class="mb-4" role="alert">{{ editError }}</v-alert>
            <v-text-field v-model="editDraft.fullName" label="Full name" maxlength="200" required :disabled="savingUser" />
            <v-text-field v-model="editDraft.email" label="Email (login)" type="email" maxlength="256" required :disabled="savingUser" hint="The user will sign in with this email address." persistent-hint />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn :disabled="savingUser" @click="editOpen = false">Cancel</v-btn>
            <v-btn class="save-action" type="submit" color="primary" :loading="savingUser" :disabled="savingUser">Save changes</v-btn>
          </v-card-actions>
        </form>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import { showConfirm } from '@/utils/messages';

import * as userApi from "@/api/users";

export default {
  name: "TeamUserManager",
  data() {
    return {
      passwordOpen: false,
      passwordUser: null,
      newPassword: '',
      confirmPassword: '',
      passwordError: '',
      savingPassword: false,
      editOpen: false,
      editError: '',
      savingUser: false,
      editDraft: { id: '', fullName: '', email: '' },
      error: '',
      success: '',
      creating: false,
      canManageUsers: false,
      newUser: {
        name: "",
        email: "",
        password: "",
      },
      users: [],
      userHeaders: [
        { title: "Name", key: "fullName" },
        { title: "Email", key: "email" },
        { title: "Role", key: "role", sortable: false },
        { title: "", key: "delete", sortable: false },
      ],
      availableRoles: ["User", "Manager", "Admin"],
    };
  },
  methods: {
    openPasswordChange(user) {
      this.passwordUser = { id: user.id, fullName: user.fullName, email: user.email };
      this.newPassword = ''; this.confirmPassword = ''; this.passwordError = '';
      this.passwordOpen = true;
    },
    closePasswordChange() {
      if (this.savingPassword) return;
      this.passwordOpen = false; this.passwordUser = null;
      this.newPassword = ''; this.confirmPassword = ''; this.passwordError = '';
    },
    async savePassword() {
      if (this.savingPassword || !this.passwordUser) return;
      if (this.newPassword.length < 6 || this.newPassword.length > 128) {
        this.passwordError = 'Use a password between 6 and 128 characters.'; return;
      }
      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = 'The passwords do not match.'; return;
      }
      this.savingPassword = true; this.passwordError = '';
      try {
        await userApi.changeUserPassword(this.passwordUser.id, this.newPassword);
        this.passwordOpen = false; this.passwordUser = null;
        this.newPassword = ''; this.confirmPassword = '';
        this.error = ''; this.success = 'Password changed. The user can sign in with their new password.';
      } catch (err) { this.passwordError = this.userError(err); }
      finally { this.savingPassword = false; }
    },
    editUser(user) {
      this.editDraft = { id: user.id, fullName: user.fullName || '', email: user.email || '' };
      this.editError = '';
      this.editOpen = true;
    },
    async saveUser() {
      if (this.savingUser) return;
      const fullName = this.editDraft.fullName.trim(), email = this.editDraft.email.trim();
      if (!fullName || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.editError = 'Enter a full name and valid email address.';
        return;
      }
      this.savingUser = true;
      this.editError = '';
      try {
        await userApi.updateUser(this.editDraft.id, { fullName, email });
        this.users = this.users.map(u => u.id === this.editDraft.id ? { ...u, fullName, email, userName: email } : u);
        this.editOpen = false;
        this.error = '';
        this.success = 'User updated.';
      } catch (err) { this.editError = this.userError(err); }
      finally { this.savingUser = false; }
    },
    userError(err) {
      const data = err?.response?.data;
      if (typeof data === 'string' && data.trim()) return data;
      if (Array.isArray(data)) return data.map(e => e.description || e.message).filter(Boolean).join(' ') || 'Could not create the user.';
      if (data?.errors) return Object.values(data.errors).flat().join(' ');
      if (err?.response?.status === 403) return 'You do not have permission for this action in the selected customer account.';
      if (err?.response?.status === 401) return 'Your session has expired. Please sign in again.';
      return data?.message || 'The request failed. Please try again.';
    },
    async loadUsers() {
      try { this.users = (await userApi.getUsers()).map(u => ({ ...u, fullName: u.fullName || u.userName })); }
      catch (err) { this.error = this.userError(err); }
    },
    async createUser() {
      if (this.creating) return;
      this.error = ''; this.success = '';
      const name = this.newUser.name.trim(), email = this.newUser.email.trim(), password = this.newUser.password;
      if (!name || !email || !password) { this.error = 'Enter a full name, email address and password.'; return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { this.error = 'Enter a valid email address.'; return; }
      this.creating = true;
      try {
        await userApi.createUser({ userName: email, fullName: name, email, password, role: 'User' });
        this.newUser = { name: '', email: '', password: '' };
        this.success = 'User created. They can log in using their email address.';
        await this.loadUsers();
      } catch (err) { this.error = this.userError(err); }
      finally { this.creating = false; }
    },
    async onRoleChange(user, newRole) {
      if (!user || !newRole || user.role === newRole) return;

      try {
        await userApi.setUserRole(user.id, newRole);
        user.role = newRole; // update UI manually
        this.$toast?.success?.("Role updated") ?? console.log("Role updated");
      } catch (err) {
        this.$toast?.error?.("Failed to update role") ?? console.error("Role update failed", err);
      }
    },
    async onDeleteUser(user) {
      if (!user?.id) return;
      if (!(await showConfirm(`Delete "${user.userName || user.email}"?`))) return;

      try {
        await userApi.deleteUser(user.id);
        this.users = this.users.filter((u) => u.id !== user.id);
      } catch (err) {
        this.$toast?.error?.("Failed to delete user") ?? console.error("Delete failed", err);
      }
    },
  },
  async mounted() {
    try { this.canManageUsers = (await userApi.getUserPermissions()).canManageUsers === true; }
    catch (err) { this.error = this.userError(err); }
    await this.loadUsers();
  },
};
</script>
<style scoped>
.team-users-page { max-width: 1480px; margin: 0 auto; padding: 28px clamp(16px, 2.4vw, 34px) 42px; }
.team-users-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.team-users-heading .portal-eyebrow { margin: 0 0 5px; color: #1c7773; }
.team-users-heading h1 { margin: 0; color: #142f38; font: 700 25px/1.12 var(--ww-font-heading, Georgia, serif); }
.team-users-heading p:last-child { max-width: 660px; margin: 7px 0 0; color: #60716f; font-size: 14px; line-height: 1.5; }
.team-users-count { display: flex; align-items: center; gap: 10px; min-width: 108px; padding: 11px 15px; border: 1px solid #b9d5ce; border-radius: 10px; background: linear-gradient(145deg, #edf7f4, #fff); color: #176b67; box-shadow: 0 4px 14px rgba(19, 73, 70, .08); }
.team-users-count > .v-icon { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 8px; background: #176b67; color: #fff; }
.team-users-count span { display: flex; align-items: baseline; gap: 4px; }.team-users-count strong { color: #153e42; font-size: 23px; line-height: 1.1; }.team-users-count small { color: #526964; font-size: 12px; }
.team-users-layout { display: grid; gap: 16px; }
.team-users-card { overflow: hidden; border: 1px solid #d5e2de; border-radius: 11px !important; background: #fff; box-shadow: 0 4px 16px rgba(20, 55, 57, .06); }
.team-users-card__title { display: flex; align-items: center; gap: 11px; min-height: 76px; padding: 15px 18px !important; border-bottom: 1px solid #e2ece9; background: #f8fbfa; }
.team-users-card__icon { display: grid; place-items: center; flex: 0 0 39px; width: 39px; height: 39px; border: 1px solid #b9d5ce; border-radius: 9px; background: #edf7f4; color: #19746e; }
.team-users-card__title strong, .team-users-card__title small { display: block; }.team-users-card__title strong { color: #173d44; font-size: 16px; font-weight: 750; }.team-users-card__title small { margin-top: 2px; color: #6b7a76; font-size: 12px; }
.team-users-card__body { padding: 18px !important; }.team-users-intro { margin: 0 0 16px; color: #5f706c; font-size: 14px; }
.team-users-form { display: grid; grid-template-columns: minmax(170px, .8fr) minmax(230px, 1fr) minmax(260px, 1.2fr); gap: 14px; align-items: start; }
.team-users-form :deep(.v-field) { min-height: 48px; border-radius: 7px; background: #fff; }.team-users-form :deep(.v-field__outline) { color: #bccdc8; }.team-users-form :deep(.v-field--focused .v-field__outline) { color: #176b67; }
.team-users-form__actions { display: flex; justify-content: flex-end; margin-top: 18px; }.team-users-form__actions .v-btn { min-height: 44px; border-radius: 7px; font-weight: 750; letter-spacing: 0; text-transform: none; }
.team-users-table { border: 0; }.team-users-table :deep(thead th) { height: 45px; background: #fbfcfc; color: #687875 !important; font-size: 11px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; }.team-users-table :deep(tbody td) { color: #263f43; }.team-users-table :deep(tbody tr:hover) { background: #f7fbfa; }.team-users-table :deep(tbody td:last-child) { width: 1%; white-space: nowrap; }
.team-users-actions { display: inline-flex; align-items: center; overflow: hidden; border: 1px solid #c8d8d3; border-radius: 7px; background: #fff; }.team-users-actions :deep(.v-btn) { min-height: 34px; padding-inline: 9px; border-radius: 0; font-size: 12px; font-weight: 700; letter-spacing: 0; text-transform: none; }.team-users-actions :deep(.v-btn + .v-btn) { border-left: 1px solid #d8e3e0; }
@media (max-width: 800px) { .team-users-page { padding: 20px 14px 32px; }.team-users-heading { align-items: flex-start; flex-direction: column; gap: 14px; }.team-users-count { width: 100%; }.team-users-form { grid-template-columns: 1fr; }.team-users-form__actions .v-btn { width: 100%; }.team-users-actions { flex-wrap: wrap; }.team-users-table :deep(tbody td:last-child) { width: auto; white-space: normal; } }
</style>
